<template>
  <div class="modern-master-view pb-12">
    <!-- Premium Global Header -->
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
          <v-icon color="white">fas fa-file-invoice-dollar</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">Documentos Recibidos</div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Gestión y control de facturas de proveedores</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center gap-3">
        <Can I="read" a="PurchaseInvoice">
          <v-btn 
            outlined
            color="primary"
            class="font-weight-black text-uppercase tracking-widest rounded-lg px-6 h-44 shadow-xs"
            :loading="exporting"
            @click="exportToExcel"
          >
            <v-icon left small>fas fa-file-excel</v-icon> Exportar
          </v-btn>
        </Can>
        <Can I="create" a="PurchaseInvoice">
          <v-btn 
            color="primary" 
            class="font-weight-black text-uppercase tracking-widest rounded-lg px-6 h-44 shadow-sm" 
            @click="showSyncDialog = true"
          >
            <v-icon left small>fas fa-cloud-download-alt</v-icon> Sincronizar SimpleFactura
          </v-btn>
        </Can>
        <Can I="create" a="PurchaseInvoice">
          <v-btn 
            color="success" 
            class="font-weight-black text-uppercase tracking-widest rounded-lg px-8 h-44 shadow-sm" 
            @click="$router.push({name:'CreatePurchaseInvoice',params:{id:'new'}})"
          >
            <v-icon left small>fas fa-plus</v-icon> Registrar Documento
          </v-btn>
        </Can>
      </div>
    </v-row>

    <!-- Navigation / Metrics (Placeholder if API supports them in the future) -->
    <!-- Mantenemos la estructura de Invoices.vue pero comentada o con datos de la grilla si no hay totales en el res -->
    <v-row v-if="results_total > 0" class="mb-8 px-4">
      <v-col cols="12" md="6">
        <v-card outlined class="modern-card pa-6 d-flex align-center border-left-primary">
          <v-avatar color="primary lighten-5" size="48" class="mr-4">
            <v-icon color="primary">fas fa-calculator</v-icon>
          </v-avatar>
          <div>
            <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Monto Total Seleccionado</div>
            <div class="text-h5 font-weight-black primary--text">{{ results_total | moneda }}</div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card outlined class="modern-card pa-6 d-flex align-center border-left-success">
          <v-avatar color="success lighten-5" size="48" class="mr-4">
            <v-icon color="success">fas fa-hand-holding-usd</v-icon>
          </v-avatar>
          <div class="flex-grow-1">
            <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Monto Pagado</div>
            <div class="d-flex align-center justify-space-between">
              <div class="text-h5 font-weight-black success--text">{{ results_payed | moneda }}</div>
              <v-chip v-if="results_total > 0" x-small color="success lighten-4" class="success--text font-weight-black px-2">
                {{ (results_payed / results_total) | percent }} del total
              </v-chip>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters Section -->
    <v-card outlined class="modern-card pa-6 mb-8 shadow-sm">
      <v-row dense align="center">
        <v-col cols="12" md="2">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Desde</div>
          <v-text-field 
            v-model='filter.received_after' 
            outlined dense hide-details clearable type="date"
            class="modern-input"
            @change="()=>{ options.page=1; fetch() }"
          />
        </v-col>
        <v-col cols="12" md="2">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Hasta</div>
          <v-text-field 
            v-model='filter.received_before' 
            outlined dense hide-details clearable type="date"
            class="modern-input"
            @change="()=>{ options.page=1; fetch() }"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Estatus</div>
          <v-select
            v-model="filter.estatus"
            :items="estatuses"
            item-text="text"
            item-value="value"
            placeholder="Todos"
            outlined dense hide-details clearable
            class="modern-input"
            prepend-inner-icon="fas fa-filter"
            @change="()=>{ options.page=1; fetch() }"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Tipo Documento</div>
          <v-select
            v-model="filter.tipo"
            :items="tipos"
            item-text="text"
            item-value="value"
            placeholder="Todos"
            outlined dense hide-details clearable
            class="modern-input"
            prepend-inner-icon="fas fa-tags"
            @change="()=>{ options.page=1; fetch() }"
          />
        </v-col>
        <v-col cols="12" md="auto" class="d-flex align-end pb-1 ml-auto">
          <v-btn color="primary" icon class="grey lighten-4 shadow-xs" @click="fetch" :loading="loading">
            <v-icon small>fas fa-sync</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Main Table Card -->
    <v-card outlined class="modern-card overflow-hidden shadow-sm">
      <v-data-table
        :headers="computedHeaders"
        :items="invoices"
        :options.sync="options"
        :server-items-length="parseInt(totalCount)"
        :loading="loading"
        class="modern-table"
        @update:options="fetch"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 20, 50, 100],
        }"
      >
        <!-- Integrated Search Slot -->
        <template v-slot:top>
          <v-sheet color="lightgrey" class="px-6 py-4 d-flex align-center border-bottom bg-lightgrey">
            <span class="caption font-weight-black uppercase tracking-widest grey--text">Documentos Recibidos: {{ totalCount }}</span>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              @keyup="searchTimeOut"
              placeholder="Buscar por número, emisor o RUT..."
              prepend-inner-icon="fas fa-search"
              outlined dense hide-details clearable
              :disabled="loading"
              class="modern-search-field"
            ></v-text-field>
          </v-sheet>
        </template>

        <!-- Cell Templates -->
        <template v-slot:item.document_id="{ item }">
          <div class="d-flex flex-column py-2">
            <div class="d-flex align-center mb-1">
              <span class="body-2 font-weight-black primary--text lh-1 mr-2">#{{ item.document_id }}</span>
              <v-chip v-if="document_types.find(e=>e.value==item.tipo)" x-small :color="document_types.find(e=>e.value==item.tipo).color + ' lighten-5'" :class="document_types.find(e=>e.value==item.tipo).color + '--text font-weight-black px-1'" label>
                {{ document_types.find(e=>e.value==item.tipo).text.substring(0,2).toUpperCase() }}
              </v-chip>
            </div>
            <v-chip x-small :color="getStatusColor(item) + ' lighten-5'" :class="getStatusColor(item) + '--text font-weight-black'" label>
              {{ getStatusName(item) }}
            </v-chip>
          </div>
        </template>

        <template v-slot:item.date_received="{ item }">
          <span class="body-2 grey--text text--darken-3 font-weight-medium">{{ item.date_received | moment("DD/MM/YYYY") }}</span>
        </template>

        <template v-slot:item.rznSocEmisor="{ item }">
          <div class="d-flex flex-column py-2">
            <span class="body-2 font-weight-bold grey--text text--darken-4 lh-1-2 mb-1">{{ item.rznSocEmisor }}</span>
            <span class="caption grey--text font-weight-black opacity-60">{{ item.rutEmisor || 'RUT S/N' }}</span>
          </div>
        </template>

        <template v-slot:item.purchase_order="{ item }">
          <div class="d-flex flex-column align-end">
            <v-chip v-if="item.purchase_order" x-small outlined color="grey darken-2" class="font-weight-black mb-1">OC #{{ item.purchase_order }}</v-chip>
            <v-chip v-if="item.work_order" x-small outlined color="primary" class="font-weight-black">OT #{{ item.work_order }}</v-chip>
            <span v-if="!item.purchase_order && !item.work_order" class="caption grey--text opacity-40">-</span>
          </div>
        </template>

        <template v-slot:item.get_total="{ item }">
          <span class="body-1 font-weight-black primary--text tracking-tighter">{{ item.get_total | moneda }}</span>
        </template>

        <template v-slot:item.total_payed="{ item }">
          <div class="d-flex flex-column align-end">
            <span :class="item.estatus === 2 ? 'success--text' : 'error--text'" class="body-2 font-weight-black">
              {{ item.get_payed | moneda }}
            </span>
            <span v-if="item.estatus !== 2" class="caption grey--text opacity-60 font-weight-bold">
              Rest: {{ (item.get_total - item.get_payed) | moneda }}
            </span>
            <v-chip v-if="item.estatus === 2" x-small color="success lighten-5" class="success--text font-weight-black mt-1" label>
              Saldada
            </v-chip>
          </div>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-menu bottom left offset-y transition="slide-y-transition" content-class="modern-card">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on" class="grey lighten-4">
                <v-icon small color="grey darken-2">fas fa-ellipsis-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense class="py-1">
              <v-list-item @click="viewReceivedPdf(item)">
                <v-list-item-icon class="mr-2">
                  <v-icon x-small color="error">fas fa-file-pdf</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="caption font-weight-bold">Ver PDF Oficial SII</v-list-item-title>
              </v-list-item>

              <Can I="update" a="PurchaseInvoice">
                <v-list-item @click="editInvoice(item)">
                  <v-list-item-icon class="mr-2">
                    <v-icon x-small color="primary">fas fa-edit</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="caption font-weight-bold">Editar Detalle</v-list-item-title>
                </v-list-item>
              </Can>
              
              <Can I="update" a="PurchaseInvoice">
                <v-list-item @click="showPayments(item)">
                  <v-list-item-icon class="mr-2">
                    <v-icon x-small color="primary">fas fa-money-bill-wave</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="caption font-weight-bold">Gestión de Pagos</v-list-item-title>
                </v-list-item>
              </Can>

              <Can I="update" a="PurchaseInvoice">
                <v-list-item v-if="item.estatus !== 2 && item.estatus !== 3" @click="markAsPaid(item)">
                  <v-list-item-icon class="mr-2">
                    <v-icon x-small color="success">fas fa-check-double</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="caption font-weight-bold success--text">Marcar como Pagada</v-list-item-title>
                </v-list-item>
              </Can>

              <Can I="update" a="PurchaseInvoice">
                <v-list-item v-if="item.estatus === 2" @click="markAsUnpaid(item)">
                  <v-list-item-icon class="mr-2">
                    <v-icon x-small color="warning">fas fa-undo</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="caption font-weight-bold warning--text text--darken-2">Marcar como Pendiente</v-list-item-title>
                </v-list-item>
              </Can>

              <v-divider class="my-1"></v-divider>

              <Can I="update" a="PurchaseInvoice">
                <v-list-item v-if="item.estatus !== 3" @click="cancelInvoice(item)" class="error--text">
                  <v-list-item-icon class="mr-2">
                    <v-icon x-small color="error">fas fa-ban</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="caption font-weight-bold">Anular Factura</v-list-item-title>
                </v-list-item>
              </Can>

              <Can I="delete" a="PurchaseInvoice">
                <v-list-item @click="deleteInvoice(item)" class="grey--text text--darken-3">
                  <v-list-item-icon class="mr-2">
                    <v-icon x-small color="grey darken-1">fas fa-trash</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="caption font-weight-bold">Eliminar</v-list-item-title>
                </v-list-item>
              </Can>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card>

    <!-- Dialogs -->
    <v-dialog v-model="showPaymentsDialog" max-width="900" content-class="modern-dialog" persistent>
      <v-card class="overflow-hidden rounded-xl bg-white">
        <div class="pa-8">
          <div class="d-flex align-center mb-6">
            <v-avatar color="success lighten-5" size="40" class="mr-4">
              <v-icon color="success" size="18">fas fa-money-bill-wave</v-icon>
            </v-avatar>
            <h2 class="text-h5 font-weight-black primary--text">Historial de Pagos - Factura #{{ invoiceToPay ? invoiceToPay.document_id : '' }}</h2>
            <v-spacer></v-spacer>
            <v-btn icon @click="showPaymentsDialog=false"><v-icon>fas fa-times</v-icon></v-btn>
          </div>
          <purchase-invoice-pay-detail 
            :purchaseInvoice="invoiceToPay" 
            @refresh="()=>{
              showPaymentsDialog=false
              fetch()
            }"
          />
        </div>
      </v-card>
    </v-dialog>

    <confirm
      v-if="invoiceToDelete"
      v-model="showConfirmDelete" 
      :message="`¿Confirma la eliminación permanente del documento #${invoiceToDelete.document_id}?`" 
      @ok="confirmDeleteInvoice"
      @cancel="showConfirmDelete=false"
    />
      
    <confirm
      v-if="invoiceToCancel"
      v-model="showConfirmCancel" 
      :message="`¿Confirma la anulación de la Factura de compra #${invoiceToCancel.document_id}?`" 
      @ok="confirmCancelInvoice"
      @cancel="showConfirmCancel=false"
    />

    <confirm
      v-if="invoiceToChangeStatus"
      v-model="showConfirmStatus" 
      :message="targetStatus === 2 ? `¿Confirma marcar la Factura #${invoiceToChangeStatus.document_id} como PAGADA?` : `¿Confirma marcar la Factura #${invoiceToChangeStatus.document_id} como PENDIENTE?`" 
      @ok="confirmChangeStatus"
      @cancel="showConfirmStatus=false"
    />

    <!-- Diálogo de Sincronización con SimpleFactura -->
    <sync-simple-factura-dialog
      v-model="showSyncDialog"
      @synced="fetch"
    />
  </div>
</template>

<script>
import { 
  fetchPurchaseInvoices, 
  deletePurchaseInvoice, 
  updatePurchaseInvoice, 
  exportPurchaseInvoicesToExcel,
  getReceivedInvoicePdf
} from '@/api/purchases'
import notifications from '@/utils/notifications'
import moment from 'moment'
import PurchaseInvoicePayDetail from '@/components/purchases/PurchaseInvoicePayDetail.vue'
import SyncSimpleFacturaDialog from '@/components/purchases/SyncSimpleFacturaDialog.vue'

export default {
  name: 'PurchaseInvoices',
  components: {
    PurchaseInvoicePayDetail,
    SyncSimpleFacturaDialog
  },
  data() {
    return {
      loading: false,
      exporting: false,
      showPaymentsDialog: false,
      showSyncDialog: false,
      invoiceToPay: null,
      showConfirmDelete: false,
      invoiceToDelete: null,
      showConfirmCancel: false,
      invoiceToCancel: null,
      showConfirmStatus: false,
      invoiceToChangeStatus: null,
      targetStatus: null,
      invoices: [],
      timer: null,
      options: {},
      search: '',
      totalCount: 0,
      results_total: 0,
      results_payed: 0,
      filter: {
        received_after: moment(new Date(new Date().getFullYear(), new Date().getMonth(), 1)).format('YYYY-MM-DD'),
        received_before: '',
        estatus: '',
        tipo: '',
      },
      document_types: [
        { value: 30, text: 'Factura', color: 'info' },
        { value: 33, text: 'Factura Electrónica', color: 'primary' },
        { value: 34, text: 'Factura Exenta', color: 'teal' },
        { value: 50, text: 'Guía de Despacho', color: 'orange' },
        { value: 52, text: 'Guía de Despacho Electrónica', color: 'orange' },
        { value: 56, text: 'Nota de Débito', color: 'purple' },
        { value: 60, text: 'Nota de Crédito', color: 'error' },
        { value: 61, text: 'Nota de Crédito Electrónica', color: 'error' }
      ],
      headers: [
        { text: 'Documento', align: 'start', value: 'document_id', class: 'table-header', width: '150px' },
        { text: 'Recibido', align: 'start', value: 'date_received', class: 'table-header', width: '120px' },
        { text: 'Razón Social / Emisor', align: 'start', value: 'rznSocEmisor', class: 'table-header' },
        { text: 'Vínculos', align: 'end', value: 'purchase_order', class: 'table-header', width: '130px', sortable: false },
        { text: 'Total Bruto', align: 'end', value: 'get_total', class: 'table-header', width: '140px' },
        { text: 'Pagado', align: 'end', value: 'total_payed', class: 'table-header', width: '140px', sortable: false },
        { text: '', align: 'end', value: 'actions', class: 'table-header', width: '60px', sortable: false }
      ],
      estatuses: [
        { value: 1, text: 'Recibida', color: 'info' },
        { value: 2, text: 'Pagada', color: 'success' },
        { value: 3, text: 'Anulada', color: 'error' }
      ],
      tipos: [
        { value: 30, text: 'Factura' },
        { value: 33, text: 'Factura Electrónica (33)' },
        { value: 34, text: 'Factura Exenta (34)' },
        { value: 50, text: 'Guía de Despacho' },
        { value: 52, text: 'Guía Despacho Electrónica (52)' },
        { value: 56, text: 'Nota de Débito (56)' },
        { value: 60, text: 'Nota de Crédito' },
        { value: 61, text: 'Nota de Crédito Electrónica (61)' }
      ]
    }
  },
  computed: {
    computedHeaders() {
      return this.headers
    }
  },
  methods: {
    async viewReceivedPdf(item) {
      try {
        notifications.showInfo('Descargando PDF desde SimpleFactura...')
        const blob = await getReceivedInvoicePdf(item.id)
        const file = new Blob([blob], { type: 'application/pdf' })
        const fileURL = URL.createObjectURL(file)
        window.open(fileURL, '_blank')
      } catch (error) {
        const msg = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message
        notifications.showError('No se pudo obtener el PDF: ' + msg)
      }
    },
    getStatusName(item) {
      return this.estatuses.find(i => i.value == item.estatus)?.text || 'Desconocido'
    },
    getStatusColor(item) {
      return this.estatuses.find(i => i.value == item.estatus)?.color || 'grey'
    },
    editInvoice(item) {
      this.$router.push({ name: 'CreatePurchaseInvoice', params: { id: item.id } })
    },
    showPayments(item) {
      this.invoiceToPay = item
      this.showPaymentsDialog = true
    },
    cancelInvoice(item) {
      this.invoiceToCancel = item
      this.showConfirmCancel = true
    },
    deleteInvoice(item) {
      this.invoiceToDelete = item
      this.showConfirmDelete = true
    },
    async confirmDeleteInvoice() {
      try {
        await deletePurchaseInvoice(this.invoiceToDelete.id)
        this.showConfirmDelete = false
        this.invoiceToDelete = null
        notifications.showSuccess('Factura eliminada correctamente')
        this.fetch()
      } catch (error) {
        notifications.showError('Error eliminando factura')
      }
    },
    async confirmCancelInvoice() {
      try {
        await updatePurchaseInvoice(this.invoiceToCancel.id, { estatus: 3 })
        this.showConfirmCancel = false
        this.invoiceToCancel = null
        notifications.showSuccess('Factura anulada correctamente')
        this.fetch()
      } catch (error) {
        notifications.showError('Error anulando factura')
      }
    },
    markAsPaid(item) {
      this.invoiceToChangeStatus = item;
      this.targetStatus = 2; // PAGADA
      this.showConfirmStatus = true;
    },
    markAsUnpaid(item) {
      this.invoiceToChangeStatus = item;
      this.targetStatus = 1; // PENDIENTE
      this.showConfirmStatus = true;
    },
    async confirmChangeStatus() {
      try {
        await updatePurchaseInvoice(this.invoiceToChangeStatus.id, { estatus: this.targetStatus })
        this.showConfirmStatus = false
        this.invoiceToChangeStatus = null
        notifications.showSuccess('Estatus modificado correctamente')
        this.fetch()
      } catch (error) {
        notifications.showError('Error actualizando estatus')
      }
    },
    async fetch() {
      this.loading = true
      const { sortBy, sortDesc, page, itemsPerPage } = this.options
      let sort = (sortBy && sortBy.length) ? sortBy[0] : '-date_received'
      
      const query = `ordering=${sortDesc && sortDesc[0] ? sort : '-' + sort}&page=${page || 1}&page_size=${itemsPerPage || 20}&search=${this.search}&estatus=${this.filter.estatus || ''}&tipo=${this.filter.tipo || ''}&received_after=${this.filter.received_after || ''}&received_before=${this.filter.received_before || ''}`
      
      try {
        const res = await fetchPurchaseInvoices(query)
        this.totalCount = res.count
        this.invoices = res.results
        // En caso de que la API de compras soporte totales agregados como la de ventas:
        this.results_total = res.total || 0
        this.results_payed = res.payed || 0
      } catch (err) {
        notifications.showError('Error cargando facturas')
      } finally {
        this.loading = false
      }
    },
    searchTimeOut() {
      if (this.timer) clearTimeout(this.timer)
      this.timer = setTimeout(() => {
        this.options.page = 1
        this.fetch()
      }, 800)
    },
    async exportToExcel() {
      this.exporting = true
      try {
        const { sortBy, sortDesc } = this.options
        let sort = (sortBy && sortBy.length) ? sortBy[0] : '-date_received'
        const query = `ordering=${sortDesc && sortDesc[0] ? sort : '-' + sort}&search=${this.search}&estatus=${this.filter.estatus || ''}&tipo=${this.filter.tipo || ''}&received_after=${this.filter.received_after || ''}&received_before=${this.filter.received_before || ''}`
        const blob = await exportPurchaseInvoicesToExcel(query)
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `facturas_compras_${new Date().toISOString().slice(0,10)}.xlsx`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        notifications.showSuccess('Archivo exportado')
      } catch (error) {
        notifications.showError('Error exportando')
      } finally {
        this.exporting = false
      }
    }
  },
  mounted() {
    this.fetch()
  },
  watch: {
    options: {
      handler() { this.fetch() },
      deep: true
    }
  }
}
</script>

<style scoped>
.modern-master-view { background-color: #f4f7f9; min-height: 100vh; }
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.04) !important; }
.shadow-xs { box-shadow: 0 1px 3px rgba(0,0,0,0.05) !important; }
.border-white-sm { border: 2px solid white !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.05) !important; }
.border-left-primary { border-left: 4px solid var(--v-primary-base) !important; }
.border-left-success { border-left: 4px solid var(--v-success-base) !important; }
.bg-lightgrey { background-color: #f8fbfc; }
.lh-1 { line-height: 1 !important; }
.lh-1-2 { line-height: 1.2 !important; }
.gap-3 { gap: 12px; }
.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 1.8px; }
.tracking-tighter { letter-spacing: -0.5px; }
.h-44 { height: 44px !important; }
.opacity-60 { opacity: 0.6; }

.modern-input >>> fieldset { border-radius: 8px !important; border-color: #e0e4e8 !important; }
.modern-search-field { width: 320px; }
.modern-search-field >>> fieldset { border-radius: 24px !important; border-color: #e0e4e8 !important; }

.modern-table >>> thead th { background-color: #f8fbfc !important; height: 56px !important; text-transform: uppercase; font-size: 0.75rem !important; letter-spacing: 1px; font-weight: 900 !important; color: #607d8b !important; }
.modern-table >>> td { height: 72px !important; border-bottom: 1px solid #f0f3f5 !important; }
.modern-table >>> tr:hover { background-color: #f4f7f9 !important; }
</style>